import { useMemo } from "react";

import type { DistributionData } from "../types";

const isLightColor = (hex: string) => {
  const clean = hex.replace("#", "");
  if (clean.length !== 6) {
    return false;
  }

  const r = parseInt(clean.slice(0, 2), 16);
  const g = parseInt(clean.slice(2, 4), 16);
  const b = parseInt(clean.slice(4, 6), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

  return luminance > 0.6;
};

type Props = {
  data: DistributionData[];
  hoverIndex: number | null;
  setHoverIndex: (value: number | null) => void;
};

export const WasteStreamDistributionLegend: React.FC<Props> = ({
  data,
  hoverIndex,
  setHoverIndex,
}) => {
  const maxValue = useMemo(
    () => Math.max(...data.map((s) => s.value), 1),
    [data],
  );

  const total = useMemo(
    () => data.reduce((sum, s) => sum + s.value, 0),
    [data],
  );

  return (
    <div className="flex min-w-[250px] flex-1 flex-col gap-[12px]">
      {data.map((s, i) => (
        <div
          key={s.label}
          onMouseEnter={() => setHoverIndex(i)}
          onMouseLeave={() => setHoverIndex(null)}
          className="flex cursor-default items-center gap-[10px] transition-opacity"
          style={{
            opacity: hoverIndex == null || hoverIndex === i ? 1 : 0.45,
          }}
        >
          <span
            className="h-[9px] w-[9px] flex-none rounded-sm"
            style={{
              background: s.color,
              border: isLightColor(s.color)
                ? "1px solid rgba(0,0,0,0.12)"
                : "none",
            }}
          />
          <span
            title={s.label}
            className="w-[96px] flex-none truncate text-[12.5px] font-semibold"
          >
            {s.label}
          </span>
          <div className="h-[7.425px] min-w-[20px] flex-1 overflow-hidden rounded-full bg-[#F1EAEA]">
            <div
              className="h-full rounded-full"
              style={{
                width: `${(s.value / maxValue) * 100}%`,
                background: s.color,
              }}
            />
          </div>
          <span className="font-signifier w-[44px] flex-none text-right text-[17px] leading-none">
            {total ? Math.round((s.value / total) * 100) : 0} %
          </span>
          <span className="text-grey6 w-[66px] flex-none text-right text-[11.5px]">
            {s.value.toLocaleString()} kg
          </span>
        </div>
      ))}
    </div>
  );
};
